import { useState } from "react";
import { X, Send, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HubSpot Configuration
const HUBSPOT_PORTAL_ID = "244712680";
const HUBSPOT_FORM_GUID = "c2843802-1cee-4dda-99c9-94acca3b5449";

export const PropertyContactModal = ({ isOpen, onClose, property }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: property 
      ? `I'm interested in the property at ${property.address}, ${property.city}, ${property.state} ${property.zipCode} (${property.price ? `Listed at $${property.price.toLocaleString()}` : 'Price TBD'}).`
      : ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const submitToHubSpot = async (data) => {
    const hubspotEndpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`;
    
    const hubspotData = {
      fields: [
        { name: "firstname", value: data.name },
        { name: "email", value: data.email },
        { name: "phone", value: data.phone || "" },
        { name: "message", value: data.message }
      ],
      context: {
        pageUri: window.location.href,
        pageName: property 
          ? `Property Inquiry - ${property.address}`
          : "Property Inquiry - Search Listings"
      }
    };

    try {
      await fetch(hubspotEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(hubspotData)
      });
    } catch (error) {
      console.error("HubSpot submission error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await Promise.all([
        axios.post(`${API}/contact`, formData),
        submitToHubSpot(formData)
      ]);
      
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message", {
        description: "Please try again or call us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      message: property 
        ? `I'm interested in the property at ${property.address}, ${property.city}, ${property.state} ${property.zipCode}.`
        : ""
    });
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-[#0F172A]/70 backdrop-blur-sm z-[100]"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-lg bg-white p-8 shadow-2xl z-[101] max-h-[90vh] overflow-y-auto"
        data-testid="property-contact-modal"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#94A3B8] hover:text-[#0F172A] transition-colors"
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        {isSubmitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#D4AF37] flex items-center justify-center mx-auto mb-6">
              <Send className="text-[#0F172A]" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-[#0F172A] mb-4 font-serif">
              Message Sent!
            </h3>
            <p className="text-[#475569] mb-6">
              Thank you for your interest. We'll get back to you as soon as possible, typically within 24 hours.
            </p>
            <button
              onClick={handleClose}
              className="btn-primary"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-[#0F172A] font-serif">
                Send a Message
              </h3>
              {property && (
                <p className="text-[#475569] mt-2">
                  Inquiring about: <span className="font-medium text-[#0F172A]">{property.address}</span>
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="modal-name" className="block text-sm font-medium text-[#0F172A] mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  id="modal-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="modal-email" className="block text-sm font-medium text-[#0F172A] mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="modal-phone" className="block text-sm font-medium text-[#0F172A] mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="modal-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="modal-message" className="block text-sm font-medium text-[#0F172A] mb-1">
                  Message *
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="form-input resize-none"
                  placeholder="Tell us about your interest in this property..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center"
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>

              <div className="text-center">
                <p className="text-sm text-[#94A3B8] mb-2">Or call us directly</p>
                <a 
                  href="tel:919-879-5636" 
                  className="text-[#D4AF37] font-semibold hover:underline flex items-center justify-center gap-2"
                >
                  <Phone size={16} />
                  (919) 879-5636
                </a>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
};
